import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import parser from 'html-react-parser';
import { Regulation } from 'services/register/regulation/interfaces/IRegulation';
import logoImg from 'assets/images/logo.png';
import {
  getAllRegulations,
  getRegulationById,
  acceptRegulation,
  fetchAgreementTerm,
} from 'services/register/regulation';
import getUrlToForceDownload from 'services/storage/getUrlToForceDownload';
import Loading from './Loading';
import {
  Container,
  Content,
  RegulationContent,
  StyledButtonConfirm,
  Modal,
  Header,
  StyledAgreementTermPending,
  BoxAccept,
} from './styles';
import AgreementTerm from '../AgreementTerm';

const producerPrivacyPolicy = getUrlToForceDownload({
  filename: 'Portal_Juntos_FMC-POLITICA_DE_PRIVACIDADE.pdf',
  url:
    'https://storage.vendavall.com.br/photos/1622492212.60b54434f2f525.21535283.pdf',
});
const producerTermsOfUse = getUrlToForceDownload({
  filename: 'Portal_Juntos_FMC-TERMO_DE_USO.pdf',
  url:
    'https://storage.vendavall.com.br/photos/1622492254.60b5445e8d30e9.15840996.pdf',
});

interface Props {
  opened: boolean;
}

const AllRegulationsOneByOne: React.FC<Props> = ({ opened }) => {
  const { addToast } = useToast();
  const { refreshParticipant, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [canAccept, setCanAccept] = useState(false);
  const [, setShowModal] = useState(false);
  const [regulations, setRegulations] = useState<Omit<Regulation, 'content'>[]>(
    [],
  );
  const [selectedRegulationIndex, setSelectedRegulationIndex] = useState(-1);
  const [regulation, setRegulation] = useState<Regulation | null>(null);
  const [agreementTermIsApproved, setAgreementTermIsApproved] = useState(false);
  const [hasPendingAgreementTerm, setHasPendingAgreementTerm] = useState(false);

  useEffect(() => {
    getAllRegulations().then(data => {
      const regulationsToAccept = data.filter(item => !item.accepted);

      if (regulationsToAccept.length > 0) {
        setRegulations(regulationsToAccept);
        setShowModal(true);
        setAgreementTermIsApproved(false);
        setHasPendingAgreementTerm(false);
      }
    });
  }, []);

  useEffect(() => {
    if (regulations.length > 0) {
      setSelectedRegulationIndex(0);
    }
  }, [regulations]);

  useEffect(() => {
    if (selectedRegulationIndex === -1) return;
    if (!regulations) return;
    setLoading(true);

    const selectedRegulation = regulations[selectedRegulationIndex];

    const isAgreementTerm = selectedRegulation.type === 'safra_term';

    if (isAgreementTerm) {
      fetchAgreementTerm(selectedRegulation.id).then(resp => {
        const hasPending = resp.list_agreement_terms.filter(
          term => term.approved === null,
        );

        if (hasPending.length > 0) {
          setHasPendingAgreementTerm(true);
        }

        const hasApproved = resp.list_agreement_terms.filter(
          term => term.approved === true,
        );

        if (hasApproved.length > 0) {
          setAgreementTermIsApproved(true);
        }
      });
    }

    setTimeout(() => {
      getRegulationById(selectedRegulation.id)
        .then(data => {
          setRegulation(data);
          setCanAccept(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 1000);
  }, [selectedRegulationIndex, regulations]);

  const handleAcceptClick = useCallback(async () => {
    if (!regulation) return;
    setAccepting(true);
    try {
      setCanAccept(false);
      await acceptRegulation(regulation.id, regulation.version);
      setSelectedRegulationIndex(i => {
        if (i >= regulations.length - 1) {
          setShowModal(false);
          refreshParticipant();
          return i;
        }
        return i + 1;
      });
    } catch {
      addToast({
        title:
          'Falha ao aceitar regulamento. Favor entrar em contato com o suporte',
        type: 'error',
      });
    }
    setAccepting(false);
  }, [regulation, addToast, regulations, refreshParticipant]);

  const acceptAction = useMemo(
    () => (
      <>
        <BoxAccept>
          {regulation?.type !== 'promotion_regulation' && (
            <input
              type="checkbox"
              name="test"
              disabled={loading}
              checked={canAccept}
              onChange={() => {
                setCanAccept(e => !e);
              }}
            />
          )}
          {regulation?.type === 'regulation_of_campaign' && (
            <span>
              AO CLICAR NO ITEM “LI E ACEITO OS TERMOS DO REGULAMENTO” IMPLICARÁ
              NO RECONHECIMENTO, DE QUE LERAM, ENTENDERAM E ACEITARAM
              INCONDICIONALMENTE TODAS AS DISPOSIÇÕES CONSTANTES DO REGULAMENTO
              DO PROGRAMA JUNTOS, SEM RESERVAS.
            </span>
          )}
          {regulation?.type === 'safra_term' && (
            <span>
              AO CLICAR NO ITEM “LI E ACEITO O TERMO SAFRA” IMPLICARÁ NO
              RECONHECIMENTO, DE QUE LERAM, ENTENDERAM E ACEITARAM
              INCONDICIONALMENTE TODAS AS DISPOSIÇÕES CONSTANTES DO TERMO SAFRA,
              SEM RESERVAS.
            </span>
          )}
          {regulation?.type === 'data_term_commercial_team' && (
            <span>
              Ao clicar na caixa “Li e aceito os{' '}
              <a href="https://juntosfmc-adm.vendavall.com.br/download?name=Portal_Juntos_FMC-TERMO_DE_USO.pdf&url=https://storage.juntosfmc.com.br/avatar/1597870012.5f3d8fbc16bc67.86487857.pdf">
                Termos de Uso
              </a>{' '}
              e{' '}
              <a href="https://juntosfmc-adm.vendavall.com.br/download?name=Portal_Juntos_FMC-POL%C3%8DTICA_DE_PRIVACIDADE.pdf&url=https://storage.juntosfmc.com.br/avatar/1598359154.5f450672673807.86869023.pdf">
                Política de Privacidade
              </a>
              ”, tenho compreensão e estou de acordo com esses Termos, bem como
              concordo e autorizo para a finalidade a que se destina, sem
              reservas.
            </span>
          )}
          {regulation?.type === 'data_term_produtores' && (
            <div className="producer-box">
              <span>
                Ao clicar na caixa “Li e aceito os{' '}
                <a href={producerTermsOfUse}>Termos de Uso</a> e{' '}
                <a href={producerPrivacyPolicy}>Política de Privacidade</a>
                ”, tenho compreensão e estou de acordo com esses Termos, bem
                como concordo e autorizo para a finalidade a que se destina, sem
                reservas.
              </span>
            </div>
          )}
        </BoxAccept>
        {regulation?.type !== 'promotion_regulation' && (
          <StyledButtonConfirm
            type="button"
            buttonRole="primary"
            onClick={handleAcceptClick}
            loading={accepting}
            disabled={!canAccept || loading}
          >
            Aceito participar
          </StyledButtonConfirm>
        )}

        {regulation?.type === 'promotion_regulation' && (
          <StyledButtonConfirm
            type="button"
            buttonRole="primary"
            onClick={handleAcceptClick}
            loading={accepting}
          >
            OK
          </StyledButtonConfirm>
        )}
      </>
    ),
    [accepting, handleAcceptClick, canAccept, loading, regulation],
  );

  if (loading) {
    return (
      <Modal isOpen={opened} onRequestClose={() => setShowModal(false)}>
        <Container>
          <Header>
            <img src={logoImg} alt="Logo" />
          </Header>
          <Content>
            <RegulationContent type="primary" key="loading">
              <Loading />
            </RegulationContent>
            {acceptAction}
          </Content>
        </Container>
      </Modal>
    );
  }

  if (!loading && hasPendingAgreementTerm) {
    return (
      <Modal isOpen onRequestClose={() => setShowModal(false)}>
        <Container>
          <Header>
            <img src={logoImg} alt="Logo" />
          </Header>
          <Content>
            <StyledAgreementTermPending type="primary">
              <h2>Acordo de Safra em análise.</h2>
              <div>
                Em alguns dias iremos verificar a assinatura e se estiver tudo
                certo você será avisado por email que seu acesso está liberado.
              </div>
            </StyledAgreementTermPending>
            <StyledButtonConfirm
              type="button"
              buttonRole="primary"
              onClick={signOut}
              loading={accepting}
            >
              Sair
            </StyledButtonConfirm>
          </Content>
        </Container>
      </Modal>
    );
  }

  if (
    !loading &&
    regulation?.type === 'safra_term' &&
    !hasPendingAgreementTerm &&
    !agreementTermIsApproved
  ) {
    return (
      <Modal isOpen onRequestClose={() => setShowModal(false)}>
        <Container>
          <Header>
            <img src={logoImg} alt="Logo" />
          </Header>
          <Content>
            <AgreementTerm
              regulation={regulation}
              handleIsResponsible={() => setAgreementTermIsApproved(true)}
              handleSendAgreementTerm={() => setHasPendingAgreementTerm(true)}
            />
          </Content>
        </Container>
      </Modal>
    );
  }

  return (
    <Modal isOpen={opened} onRequestClose={() => setShowModal(false)}>
      <Container>
        <Header>
          <img src={logoImg} alt="Logo" />
        </Header>
        <Content>
          <>
            <RegulationContent
              type="primary"
              key={`regulation-${regulation?.id}`}
            >
              {parser(regulation?.content || '')}
            </RegulationContent>
            {acceptAction}
          </>
        </Content>
      </Container>
    </Modal>
  );
};

export default AllRegulationsOneByOne;
